import { createSelector } from 'reselect';
import {
  at,
  compact,
  constant,
  flow,
  get,
  getOr,
  identity,
  isEqual,
  replace,
  pick,
  some,
} from 'lodash/fp';
import formatDate from 'date-fns/format';

import {
  isExclusiveFeature,
  isFinanceCalculatorFeature,
  isFinanceWidgetFeature,
  isMonthlyPriceAvailableFeature,
} from 'modules/features';
import { REGULAR_SIZES } from 'constants/imageSizePrefixes';
import getImageUrl from 'helpers/ad/getImageUrl';
import { getAdTitle } from 'helpers/ad/getAdTitle';
import { getAdUrl } from 'helpers/namedUrls';
import {
  formatDistance,
  formatFuelConsumption,
  formatNumber,
  formatPower,
  formatMoney,
  stringFormatter,
  LOCALE,
} from 'helpers/i18n';
import { labelsByValue as fuelTypeLabelsByValue } from 'helpers/fieldOptions/car/fuelType';
import { labelsByValue as transmissionLabelsByValue } from 'helpers/fieldOptions/car/transmission';
import * as sellerTypes from 'constants/sellerType';
import { AUTOHERO_HOST } from 'config';

const formatters = {
  co2Value: stringFormatter(__('search.aditem.co2Value.value')),
  fuelConsumption: stringFormatter(
    __('search.aditem.fuelConsumption.combined'),
  ),
  vatRate: stringFormatter(__('carDetails.vatRate')),
  monthlyPrice: stringFormatter(__('search.aditem.monthlyPrice.value')),
};

const getFallbackImages = (image) => {
  if (image) {
    return [
      getImageUrl(image, REGULAR_SIZES.medium),
      getImageUrl(image, REGULAR_SIZES.original),
    ];
  }

  return [];
};

const getDesktopImages = createSelector(get(['image']), (image) =>
  compact([
    getImageUrl(image, REGULAR_SIZES.medium),
    ...getFallbackImages(image),
  ]),
);

const getMobileImages = createSelector(get(['image']), (image) =>
  compact([
    getImageUrl(image, REGULAR_SIZES.small),
    ...getFallbackImages(image),
  ]),
);

const getIsExclusive = (ad) => ad.exclusive && isExclusiveFeature;
const getFormattedCo2Value = (ad) =>
  ad.co2Value
    ? formatters.co2Value({ co2Value: formatNumber(ad.co2Value) })
    : '';
const getFuel = ({ fuelType }) => fuelTypeLabelsByValue[fuelType];
const getGear = ({ gearType }) => transmissionLabelsByValue[gearType];
const getformattedFuelConsumption = flow(
  get(['fuelConsumption', 'combined']),
  (value) =>
    value
      ? formatters.fuelConsumption({
          fuelConsumption: formatFuelConsumption(value),
        })
      : '',
);

const getMileage = ({ mileage }) => formatDistance(mileage);
const getFormattedHorsePower = ({ horsePower: hp, kw }) =>
  formatPower({ hp, kw });

const getRegistration = flow(
  at(['registration', 'firstRegistrationYear']),
  ([registration, firstRegistrationYear]) =>
    registration ? formatDate(registration, 'MM/YYYY') : firstRegistrationYear,
);

const getNanoImage = flow(
  at(['image', 'sellerType']),
  ([image, sellerType]) => {
    return sellerType === sellerTypes.RETAIL
      ? getImageUrl(image, REGULAR_SIZES.nano)
      : undefined;
  },
);

const getVatRate = createSelector(get(['vatRate']), (vatRate) =>
  vatRate ? formatters.vatRate({ vatRate: vatRate * 100 }) : '',
);

const or = (...args) => some(identity, args);
const getSellerType = get(['sellerType']);
const isPrivate = flow(getSellerType, isEqual(sellerTypes.PRIVATE_SALE));
const isPrivateListing = flow(
  getSellerType,
  isEqual(sellerTypes.PRIVATE_LISTING_SELLER),
);
const isMerchant = flow(getSellerType, isEqual(sellerTypes.MERCHANT));
const isRetail = flow(getSellerType, isEqual(sellerTypes.RETAIL));

const withLocation = createSelector(
  isMerchant,
  isPrivate,
  isPrivateListing,
  or,
);
const withConsumption = createSelector(
  getFormattedCo2Value,
  getformattedFuelConsumption,
  or,
);

const getSellerName = createSelector(
  get(['sellerName']),
  isMerchant,
  (sellerName, isMerchant) => (sellerName && isMerchant ? sellerName : ''),
);

const getMonthlyPrice = flow(get(['monthlyPrice']), Math.round);

const getFormattedMonthlyPrice = createSelector(
  getMonthlyPrice,
  (monthlyPrice) =>
    monthlyPrice
      ? formatters.monthlyPrice({
          monthlyPrice: formatMoney(monthlyPrice),
        })
      : null,
);

const getShowFinanceCalculator = createSelector(
  isMerchant,
  getMonthlyPrice,
  (isMerchant, monthlyPrice) =>
    isFinanceCalculatorFeature && isMerchant && monthlyPrice > 0,
);

const getShowFinanceWidget = createSelector(
  getShowFinanceCalculator,
  isRetail,
  (showFinanceCalculator, isRetail) => {
    return isFinanceWidgetFeature && !isRetail && !showFinanceCalculator;
  },
);

const getShowFinanceCalculatorLink = createSelector(
  constant(isMonthlyPriceAvailableFeature),
  getShowFinanceCalculator,
  getShowFinanceWidget,
  (isMonthlyPriceAvailable, showFinanceCalculator, showFinanceWidget) =>
    isMonthlyPriceAvailable && (showFinanceCalculator || showFinanceWidget),
);

const getHas360Images = (ad) => ad.has360Images;

const getRetailAdUrl = createSelector(
  identity,
  constant(`${AUTOHERO_HOST}/${LOCALE}`),
  (ad, domain) => getAdUrl({ ...ad, ignorePathTitle: true }, domain),
);

const getEmailContactUrl = createSelector(
  getAdUrl,
  (adUrl) => `${adUrl}?scrollToContactForm=true`,
);

const getSellerPhone = get(['contact', 'phone']);

const isPhoneVisible = createSelector(
  pick(['contact', 'sellerType']),
  ({ contact, sellerType }) => {
    let showPhone = false;

    if (sellerType === sellerTypes.PRIVATE_LISTING_SELLER) {
      showPhone = Boolean(contact && contact.phone && contact.displayPhone);
    } else {
      showPhone = Boolean(contact && contact.phone);
    }

    return showPhone;
  },
);

const getPhone = createSelector(
  getSellerPhone,
  isPhoneVisible,
  (phone, isPhoneVisible) => (isPhoneVisible ? phone : null),
);

const getPhoneUrl = createSelector(
  getAdUrl,
  isPhoneVisible,
  (adUrl, isPhoneVisible) => {
    return isPhoneVisible ? `${adUrl}?scrollToPhone=true` : null;
  },
);

const getSellerContactInfoProps = createSelector(
  getAdUrl,
  isRetail,
  getPhoneUrl,
  getEmailContactUrl,
  getRetailAdUrl,
  getPhone,
  pick(['trackAs']),
  (
    adUrl,
    isRetail,
    phoneUrl,
    emailContactUrl,
    retailAdUrl,
    phone,
    adProps,
  ) => ({
    adUrl,
    phoneUrl,
    emailContactUrl,
    retailAdUrl,
    isRetail,
    phone,
    ...adProps,
  }),
);

export default {
  monthlyPrice: getMonthlyPrice,
  sellerName: getSellerName,
  withLocation,
  withConsumption,
  isRetail,
  sellerLogo: flow(
    getOr('', ['sellerLogo', 'url']),
    replace('{size}', REGULAR_SIZES.logo),
  ),
  desktopImages: getDesktopImages,
  exclusive: getIsExclusive,
  formattedCo2Value: getFormattedCo2Value,
  formattedFuelConsumption: getformattedFuelConsumption,
  formattedMileage: getMileage,
  formattedHorsePower: getFormattedHorsePower,
  formattedRegistration: getRegistration,
  formattedMonthlyPrice: getFormattedMonthlyPrice,
  fuel: getFuel,
  gear: getGear,
  link: isRetail ? getRetailAdUrl : getAdUrl,
  mobileImages: getMobileImages,
  nanoImage: getNanoImage,
  title: getAdTitle,
  vatRate: getVatRate,
  has360Images: getHas360Images,
  showFinanceCalculatorLink: getShowFinanceCalculatorLink,
  showMonthlyPrice: constant(isMonthlyPriceAvailableFeature),
  sellerContactInfoProps: getSellerContactInfoProps,
};
